/**
 * BOSS Avans Yönetim Sayfası.
 * Sekme: Bekleyen / Karşı Teklif / Onaylı (ödeme bekliyor) / Geçmiş
 */

import { useEffect, useState, useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import {
  CreditCard, Clock, RefreshCw, CheckCircle2, History,
  AlertCircle,
} from 'lucide-react'
import {
  tumAvanslar, avansOdendi, type Avans,
} from '@/lib/services/avans-service'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { useAuth } from '@/contexts/AuthContext'
import { useYetki } from '@/hooks/useYetki'
import { YETKILER } from '@/lib/yetkiler'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { AvansKararModal } from '@/components/avans/AvansKararModal'
import { AvansKararDegistirModal } from '@/components/avans/AvansKararDegistirModal'
import type { KullaniciPersonel } from '@/types'

type Sekme = 'bekleyen' | 'karsiTeklif' | 'onayli' | 'gecmis'

export function AvanslarPage() {
  const { firebaseUser, kullanici } = useAuth()
  const listeleyebilir = useYetki(YETKILER.AVANS_LISTE_GOR)
  const onaylayabilir = useYetki(YETKILER.AVANS_ONAYLA)
  const odeIsaretleyebilir = useYetki(YETKILER.AVANS_ODE_ISARETLE)
  const isBoss = kullanici?.rol === 'boss'

  const benimSubeIdleri = useMemo(() => {
    if (isBoss) return null  // null = sınırsız (BOSS tüm avansları görür)
    if (kullanici?.rol !== 'personel') return [] as string[]
    return (kullanici.subeIdleri ?? []) as string[]
  }, [kullanici, isBoss])

  const [avanslar, setAvanslar] = useState<Avans[]>([])
  const [personeller, setPersoneller] = useState<KullaniciPersonel[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [sekme, setSekme] = useState<Sekme>('bekleyen')

  const [karar, setKarar] = useState<{ avans: Avans; personel: KullaniciPersonel } | null>(null)
  const [odendiOnayi, setOdendiOnayi] = useState<Avans | null>(null)
  const [kararDegistir, setKararDegistir] = useState<{ avans: Avans; personel: KullaniciPersonel } | null>(null)

  async function yukle() {
    try {
      setYukleniyor(true)
      const [a, p] = await Promise.all([tumAvanslar(), tumPersonelleriGetir()])
      // Şube filtresi: yönetici sadece kendi şubesindeki personellerin avanslarını görür
      const filtreliPersoneller = benimSubeIdleri === null
        ? p
        : p.filter(per => per.subeIdleri?.some(s => benimSubeIdleri.includes(s)) ?? false)
      const izinliIdSeti = new Set(filtreliPersoneller.map(per => per.id))
      const filtreliAvanslar = benimSubeIdleri === null
        ? a
        : a.filter(av => izinliIdSeti.has(av.personelId))
      setAvanslar(filtreliAvanslar)
      setPersoneller(filtreliPersoneller)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Yükleme hatası: ' + (err as Error).message)
    } finally {
      setYukleniyor(false)
    }
  }

  useEffect(() => {
    if (listeleyebilir) yukle()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listeleyebilir])

  if (!listeleyebilir) {
    return <Navigate to="/yetkisiz" replace />
  }

  const personelHaritasi = useMemo(() => {
    const m = new Map<string, KullaniciPersonel>()
    personeller.forEach((p) => m.set(p.id, p))
    return m
  }, [personeller])

  const sayilar = useMemo(() => ({
    bekleyen: avanslar.filter((a) => a.durum === 'bekliyor').length,
    karsiTeklif: avanslar.filter((a) => a.durum === 'karsiTeklif').length,
    onayli: avanslar.filter((a) => a.durum === 'onaylandi').length,
    gecmis: avanslar.filter((a) =>
      a.durum === 'odendi' || a.durum === 'reddedildi' || a.durum === 'iptal'
    ).length,
  }), [avanslar])

  const filtreli = useMemo(() => {
    switch (sekme) {
      case 'bekleyen': return avanslar.filter((a) => a.durum === 'bekliyor')
      case 'karsiTeklif': return avanslar.filter((a) => a.durum === 'karsiTeklif')
      case 'onayli': return avanslar.filter((a) => a.durum === 'onaylandi')
      case 'gecmis': return avanslar.filter((a) =>
        a.durum === 'odendi' || a.durum === 'reddedildi' || a.durum === 'iptal'
      )
    }
  }, [avanslar, sekme])

  async function odendiOnay() {
    if (!odendiOnayi || !firebaseUser) return
    try {
      await avansOdendi(odendiOnayi.id, firebaseUser.uid)
      setOdendiOnayi(null)
      await yukle()
    } catch (err) {
      alert('Hata: ' + (err as Error).message)
      setOdendiOnayi(null)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <CreditCard className="w-6 h-6 text-brand-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Avanslar</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            {sayilar.bekleyen + sayilar.karsiTeklif > 0 && (
              <span className="text-amber-600 font-semibold">
                {sayilar.bekleyen + sayilar.karsiTeklif} işlem bekleniyor
              </span>
            )}
            {sayilar.bekleyen + sayilar.karsiTeklif === 0 && 'Tüm talepler işlendi'}
          </p>
        </div>
      </div>

      {/* Sekmeler */}
      <div className="flex flex-wrap gap-1.5 mb-4 bg-gray-100 p-1 rounded-xl">
        <SekmeBtn
          aktif={sekme === 'bekleyen'}
          onClick={() => setSekme('bekleyen')}
          ikon={<Clock className="w-4 h-4" />}
          etiket="Bekleyen"
          sayi={sayilar.bekleyen}
        />
        <SekmeBtn
          aktif={sekme === 'karsiTeklif'}
          onClick={() => setSekme('karsiTeklif')}
          ikon={<RefreshCw className="w-4 h-4" />}
          etiket="Karşı Teklif"
          sayi={sayilar.karsiTeklif}
        />
        <SekmeBtn
          aktif={sekme === 'onayli'}
          onClick={() => setSekme('onayli')}
          ikon={<CheckCircle2 className="w-4 h-4" />}
          etiket="Ödenecek"
          sayi={sayilar.onayli}
        />
        <SekmeBtn
          aktif={sekme === 'gecmis'}
          onClick={() => setSekme('gecmis')}
          ikon={<History className="w-4 h-4" />}
          etiket="Geçmiş"
          sayi={sayilar.gecmis}
        />
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm mb-4">
          {hata}
        </div>
      )}

      {yukleniyor ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : filtreli.length === 0 ? (
        <div className="card text-center py-12">
          <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="font-semibold text-gray-700">
            {sekme === 'bekleyen' && 'Bekleyen talep yok'}
            {sekme === 'karsiTeklif' && 'Karşı teklifte bekleyen yok'}
            {sekme === 'onayli' && 'Ödeme bekleyen yok'}
            {sekme === 'gecmis' && 'Geçmişte kayıt yok'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtreli.map((a) => (
            <AvansKarti
              key={a.id}
              avans={a}
              personel={personelHaritasi.get(a.personelId)}
              onaylayabilir={onaylayabilir}
              odeIsaretleyebilir={odeIsaretleyebilir}
              kararDegistirebilir={isBoss}
              onKararVer={() => {
                const p = personelHaritasi.get(a.personelId)
                if (p) setKarar({ avans: a, personel: p })
              }}
              onOdendi={() => setOdendiOnayi(a)}
              onKararDegistir={() => {
                const p = personelHaritasi.get(a.personelId)
                if (p) setKararDegistir({ avans: a, personel: p })
              }}
            />
          ))}
        </div>
      )}

      <AvansKararModal
        acik={!!karar}
        avans={karar?.avans ?? null}
        personel={karar?.personel ?? null}
        onKapat={() => setKarar(null)}
        onKaydedildi={() => {
          setKarar(null)
          yukle()
        }}
      />

      <AvansKararDegistirModal
        acik={!!kararDegistir}
        avans={kararDegistir?.avans ?? null}
        personel={kararDegistir?.personel ?? null}
        onKapat={() => setKararDegistir(null)}
        onKaydedildi={() => {
          setKararDegistir(null)
          yukle()
        }}
      />

      <ConfirmDialog
        acik={!!odendiOnayi}
        baslik="Ödendi Olarak İşaretle"
        mesaj={`${(odendiOnayi?.onayTutari ?? 0).toLocaleString('de-DE')} ₺ avansın ödendiğini onaylıyor musun? Bu işlem ay sonu maaşından otomatik düşülecek.`}
        onayMetni="Ödendi"
        onOnay={odendiOnay}
        onIptal={() => setOdendiOnayi(null)}
      />
    </div>
  )
}

interface SekmeBtnProps {
  aktif: boolean
  onClick: () => void
  ikon: React.ReactNode
  etiket: string
  sayi: number
}

function SekmeBtn({ aktif, onClick, ikon, etiket, sayi }: SekmeBtnProps) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${
        aktif
          ? 'bg-white text-brand-700 shadow-sm'
          : 'text-gray-600 hover:bg-gray-200'
      }`}
    >
      {ikon}
      <span className="hidden sm:inline">{etiket}</span>
      {sayi > 0 && (
        <span
          className={`px-1.5 py-0.5 rounded-full text-xs ${
            aktif ? 'bg-brand-100 text-brand-700' : 'bg-gray-300 text-gray-700'
          }`}
        >
          {sayi}
        </span>
      )}
    </button>
  )
}

interface AvansKartiProps {
  avans: Avans
  personel?: KullaniciPersonel
  onKararVer: () => void
  onOdendi: () => void
  onKararDegistir: () => void
}

function AvansKarti({ avans, personel, onKararVer, onOdendi, onKararDegistir }: AvansKartiProps) {
  const tarih = avans.talepTarihi.toDate().toLocaleDateString('tr-TR', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })

  let durumRenk = 'bg-gray-50 border-gray-200'
  let durumYazi = 'text-gray-600'
  let durumEtiket = ''

  switch (avans.durum) {
    case 'bekliyor':
      durumRenk = 'bg-amber-50 border-amber-200'
      durumYazi = 'text-amber-700'
      durumEtiket = 'Bekliyor'
      break
    case 'karsiTeklif':
      durumRenk = 'bg-purple-50 border-purple-200'
      durumYazi = 'text-purple-700'
      durumEtiket = `Karşı teklif gönderildi: ${(avans.karsiTeklifTutari ?? 0).toLocaleString('de-DE')} ₺`
      break
    case 'onaylandi':
      durumRenk = 'bg-blue-50 border-blue-200'
      durumYazi = 'text-blue-700'
      durumEtiket = 'Ödeme bekliyor'
      break
    case 'odendi':
      durumRenk = 'bg-green-50 border-green-200'
      durumYazi = 'text-green-700'
      durumEtiket = `Ödendi (${avans.odemeTarihi?.toDate().toLocaleDateString('tr-TR') ?? '?'})`
      break
    case 'reddedildi':
      durumRenk = 'bg-red-50 border-red-200'
      durumYazi = 'text-red-700'
      durumEtiket = 'Reddedildi'
      break
    case 'iptal':
      durumRenk = 'bg-gray-50 border-gray-200'
      durumYazi = 'text-gray-500'
      durumEtiket = 'İptal edildi'
      break
  }

  const tutar = avans.onayTutari ?? avans.karsiTeklifTutari ?? avans.talepTutari

  return (
    <div className={`card border-2 ${durumRenk}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-bold text-gray-900">{personel?.isim ?? 'Bilinmeyen'}</h3>
            <span className={`text-xs font-semibold ${durumYazi}`}>{durumEtiket}</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {tutar.toLocaleString('de-DE')} ₺
            {avans.durum === 'karsiTeklif' && (
              <span className="text-sm text-gray-500 line-through ml-2">
                {avans.talepTutari.toLocaleString('de-DE')} ₺
              </span>
            )}
          </p>
          {avans.aciklama && (
            <p className="text-sm text-gray-700 mt-1 italic">"{avans.aciklama}"</p>
          )}
          <p className="text-xs text-gray-500 mt-1">{tarih}</p>
          {avans.redSebebi && avans.durum === 'reddedildi' && (
            <p className="text-xs text-red-700 mt-1 bg-red-50 p-1.5 rounded inline-flex items-start gap-1">
              <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
              {avans.redSebebi}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {avans.durum === 'bekliyor' && (
            <button
              onClick={onKararVer}
              className="btn-primary text-sm py-2 px-3 whitespace-nowrap"
            >
              Karar Ver
            </button>
          )}
          {avans.durum === 'onaylandi' && (
            <button
              onClick={onOdendi}
              className="bg-green-600 hover:bg-green-700 text-white text-sm py-2 px-3 rounded-lg font-semibold whitespace-nowrap"
            >
              Ödendi
            </button>
          )}
          {(avans.durum === 'onaylandi' || avans.durum === 'reddedildi') && (
            <button
              onClick={onKararDegistir}
              className="text-xs text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400 py-1.5 px-2 rounded-lg whitespace-nowrap"
              title={avans.durum === 'onaylandi' ? 'Onayı geri al ve reddet' : 'Reddi geri al ve onayla'}
            >
              Kararı değiştir
            </button>
          )}
          {avans.durum === 'karsiTeklif' && (
            <span className="text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded-full whitespace-nowrap">
              Personel cevap bekleniyor
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
